import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Grid,
  Pagination,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  Speed as SpeedIcon,
  Add as AddIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  Timestamp,
  query,
  where,
  addDoc
} from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PAGE_SIZE = 10;
const DEFAULT_TYPES = ["Tipper", "Truck", "Tractor"];

function Vehicalmgmt() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState(DEFAULT_TYPES);
  const [newTypeName, setNewTypeName] = useState("");
  const [typeModalOpen, setTypeModalOpen] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  // Vehicle Form Modal state
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

  // Fuel logs modal state
  const [fuelDialogOpen, setFuelDialogOpen] = useState(false);
  const [selectedVehicleLogs, setSelectedVehicleLogs] = useState([]);
  const [selectedVehicleNumber, setSelectedVehicleNumber] = useState("");
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [form, setForm] = useState({
    type: "Tipper",
    vehicalNo: "",
    mfgYear: "",
    puc: "",
    permit: "",
    pucDate: "",
    permitDate: "",
    fitnessDate: "",
    roadTaxType: "Yearly", // 'One Time' or 'Yearly'
    roadTaxDate: "",
    remark: "",
  });

  useEffect(() => {
    loadVehicles();
    loadVehicleTypes();
  }, []);

  const loadVehicleTypes = async () => {
    try {
      const snap = await getDocs(collection(db, "vehicle_types"));
      if (!snap.empty) {
        const loadedTypes = snap.docs.map((d) => d.data().name).filter(Boolean);
        // Deduplicate and combine with defaults
        const merged = Array.from(new Set([...DEFAULT_TYPES, ...loadedTypes]));
        setVehicleTypes(merged);
      } else {
        setVehicleTypes(DEFAULT_TYPES);
      }
    } catch (err) {
      console.error("Error loading vehicle types:", err);
      setVehicleTypes(DEFAULT_TYPES);
    }
  };

  const handleAddVehicleType = async () => {
    const trimmed = newTypeName.trim();
    if (!trimmed) {
      Swal.fire("Warning", "Please enter a valid type name", "warning");
      return;
    }

    if (vehicleTypes.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      Swal.fire("Warning", "This type already exists", "warning");
      return;
    }

    try {
      await addDoc(collection(db, "vehicle_types"), {
        name: trimmed,
        createdAt: Timestamp.now(),
      });
      const updated = [...vehicleTypes, trimmed];
      setVehicleTypes(updated);
      setForm((prev) => ({ ...prev, type: trimmed }));
      setNewTypeName("");
      Swal.fire("Success", `Added "${trimmed}" to vehicle types`, "success");
    } catch (err) {
      console.error("Error adding vehicle type:", err);
      Swal.fire("Error", "Could not save vehicle type", "error");
    }
  };

  const handleDeleteVehicleType = async (typeName) => {
    const res = await Swal.fire({
      title: `Delete "${typeName}"?`,
      text: "Existing vehicles with this type will keep their record, but it will be removed from the options list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });

    if (!res.isConfirmed) return;

    try {
      const q = query(collection(db, "vehicle_types"), where("name", "==", typeName));
      const snap = await getDocs(q);
      const deletePromises = snap.docs.map((d) => deleteDoc(doc(db, "vehicle_types", d.id)));
      await Promise.all(deletePromises);

      const updated = vehicleTypes.filter((t) => t !== typeName);
      setVehicleTypes(updated);

      if (form.type === typeName) {
        setForm((prev) => ({ ...prev, type: updated[0] || "" }));
      }

      Swal.fire("Deleted", `"${typeName}" was removed`, "success");
    } catch (err) {
      console.error("Error deleting vehicle type:", err);
      Swal.fire("Error", "Could not delete vehicle type", "error");
    }
  };

  const loadVehicles = async () => {
    try {
      const snap = await getDocs(collection(db, "vehicles"));
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVehicles(arr);
    } catch (err) {
      console.error("Error loading vehicles:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      type: vehicleTypes[0] || "Tipper",
      vehicalNo: "",
      mfgYear: "",
      puc: "",
      permit: "",
      pucDate: "",
      permitDate: "",
      fitnessDate: "",
      roadTaxType: "Yearly",
      roadTaxDate: "",
      remark: "",
    });
    setEditingId(null);
  };

  const saveVehicle = async () => {
    if (!form.type || !form.vehicalNo) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    try {
      let id = editingId || doc(collection(db, "vehicles")).id;

      await setDoc(doc(db, "vehicles", id), {
        ...form,
        createdTime: editingId ? form.createdTime : Timestamp.now(),
      });

      Swal.fire("Success", editingId ? "Vehicle updated" : "Vehicle added", "success");

      resetForm();
      setVehicleModalOpen(false);
      loadVehicles();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const editVehicle = (v) => {
    // If the vehicle's type is custom and not currently in list, register it
    if (v.type && !vehicleTypes.includes(v.type)) {
      setVehicleTypes((prev) => [...prev, v.type]);
    }

    setForm({
      type: v.type || vehicleTypes[0] || "Tipper",
      vehicalNo: v.vehicalNo || "",
      mfgYear: v.mfgYear || "",
      puc: v.puc || "",
      permit: v.permit || "",
      pucDate: v.pucDate || "",
      permitDate: v.permitDate || "",
      fitnessDate: v.fitnessDate || "",
      roadTaxType: v.roadTaxType || "Yearly",
      roadTaxDate: v.roadTaxDate || "",
      remark: v.remark || "",
      createdTime: v.createdTime || null,
    });
    setEditingId(v.id);
    setVehicleModalOpen(true);
  };

  const deleteVehicle = async (id) => {
    const res = await Swal.fire({
      title: "Delete vehicle?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "vehicles", id));
      Swal.fire("Deleted", "Vehicle removed", "success");
      loadVehicles();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const checkFuelAndAverage = async (vehicle) => {
    setSelectedVehicleNumber(vehicle.vehicalNo);
    setFuelDialogOpen(true);
    setLoadingLogs(true);
    try {
      const q = query(
        collection(db, "fuel_entries"),
        where("vehicleId", "==", vehicle.id)
      );
      const snap = await getDocs(q);
      const logsData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      logsData.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setSelectedVehicleLogs(logsData);
    } catch (err) {
      console.error("Error fetching fuel logs for vehicle:", err);
      setSelectedVehicleLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const filtered = vehicles.filter(
    (v) =>
      v.type?.toLowerCase().includes(search.toLowerCase()) ||
      v.vehicalNo?.toLowerCase().includes(search.toLowerCase()) ||
      v.remark?.toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Box flex={1} p={3}>
        {/* ================= Top Bar ================= */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight={800}>
            Vehicle Management
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setTypeModalOpen(true)}
            >
              Manage Types
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setVehicleModalOpen(true);
              }}
            >
              Add Vehicle
            </Button>
          </Stack>
        </Stack>

        {/* ================= Search ================= */}
        <TextField
          label="Search by type, vehicle no, or remark"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{ mb: 2, bgcolor: "#fff", width: "320px" }}
          size="small"
        />

        {/* ================= Table ================= */}
        <Card>
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Sr No</b></TableCell>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Vehicle No</b></TableCell>
                  <TableCell><b>MFG Year</b></TableCell>
                  <TableCell><b>PUC</b></TableCell>
                  <TableCell><b>Permit</b></TableCell>
                  <TableCell><b>PUC Expiry</b></TableCell>
                  <TableCell><b>Permit Expiry</b></TableCell>
                  <TableCell><b>Fitness Expiry</b></TableCell>
                  <TableCell><b>Road Tax</b></TableCell>
                  <TableCell><b>Remark</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="textSecondary">
                        No vehicles found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((v, index) => (
                    <TableRow key={v.id}>
                      <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                      <TableCell>
                        <Chip label={v.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{v.vehicalNo}</TableCell>
                      <TableCell>{v.mfgYear || "-"}</TableCell>
                      <TableCell>{v.puc || "-"}</TableCell>
                      <TableCell>{v.permit || "-"}</TableCell>
                      <TableCell>{v.pucDate || "-"}</TableCell>
                      <TableCell>{v.permitDate || "-"}</TableCell>
                      <TableCell>{v.fitnessDate || "-"}</TableCell>
                      <TableCell>
                        {v.roadTaxType === "One Time" ? "One Time" : v.roadTaxDate || "-"}
                      </TableCell>
                      <TableCell>{v.remark || "-"}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SpeedIcon fontSize="small" />}
                            onClick={() => checkFuelAndAverage(v)}
                            sx={{ fontSize: "0.7rem", py: 0.2, px: 1 }}
                          >
                            Fuel/Avg
                          </Button>
                          <IconButton color="primary" size="small" onClick={() => editVehicle(v)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                          <IconButton color="error" size="small" onClick={() => deleteVehicle(v.id)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(filtered.length / PAGE_SIZE) || 1}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ================= Add / Edit Vehicle Modal ================= */}
      <Dialog open={vehicleModalOpen} onClose={() => setVehicleModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {editingId ? "Edit Vehicle" : "Add New Vehicle"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} sx={{ mt: 0.5 }}>
            {/* Dynamic Type Select */}
            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                select
                label="Type"
                name="type"
                value={form.type}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              >
                {vehicleTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle No"
                name="vehicalNo"
                value={form.vehicalNo}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="MFG Year"
                name="mfgYear"
                value={form.mfgYear}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="PUC"
                name="puc"
                value={form.puc}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Permit"
                name="permit"
                value={form.permit}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="PUC Expiry Date"
                name="pucDate"
                InputLabelProps={{ shrink: true }}
                value={form.pucDate}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Permit Expiry Date"
                name="permitDate"
                InputLabelProps={{ shrink: true }}
                value={form.permitDate}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fitness Expiry Date"
                name="fitnessDate"
                InputLabelProps={{ shrink: true }}
                value={form.fitnessDate}
                onChange={handleChange}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Road Tax Option"
                name="roadTaxType"
                value={form.roadTaxType}
                onChange={handleChange}
              >
                <MenuItem value="Yearly">Yearly</MenuItem>
                <MenuItem value="One Time">One Time</MenuItem>
              </TextField>
            </Grid>

            {form.roadTaxType === "Yearly" && (
              <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Road Tax Expiry Date"
                  name="roadTaxDate"
                  InputLabelProps={{ shrink: true }}
                  value={form.roadTaxDate}
                  onChange={handleChange}
                />
              </Grid>
            )}

            <Grid className="col-sm-4 mb-3" item xs={12} sm={form.roadTaxType === "Yearly" ? 8 : 8}>
              <TextField
                fullWidth
                size="small"
                label="Remark"
                name="remark"
                value={form.remark}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVehicleModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={saveVehicle}>
            {editingId ? "Update Vehicle" : "Add Vehicle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Manage Types Dialog ================= */}
      <Dialog open={typeModalOpen} onClose={() => setTypeModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Manage Vehicle Types</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack direction="row" spacing={1} mb={2}>
            <TextField
              label="New Type Name"
              size="small"
              fullWidth
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="e.g. Excavator, Van"
            />
            <Button variant="contained" onClick={handleAddVehicleType}>
              Add
            </Button>
          </Stack>

          <Typography variant="subtitle2" color="textSecondary" mb={1}>
            Configured Types:
          </Typography>

          <List dense sx={{ maxHeight: 220, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
            {vehicleTypes.map((t) => (
              <ListItem key={t}>
                <ListItemText primary={t} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    color="error"
                    onClick={() => handleDeleteVehicleType(t)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeModalOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Fuel & Average History Dialog ================= */}
      <Dialog open={fuelDialogOpen} onClose={() => setFuelDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Fuel & Average History (Latest First) — {selectedVehicleNumber}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {loadingLogs ? (
            <Typography align="center" py={4}>Loading fuel logs...</Typography>
          ) : selectedVehicleLogs.length === 0 ? (
            <Typography align="center" py={4} color="textSecondary">
              No fuel records found for this vehicle.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Driver</b></TableCell>
                  <TableCell><b>Open / Close Meter</b></TableCell>
                  <TableCell><b>Qty & Rate</b></TableCell>
                  <TableCell><b>Average</b></TableCell>
                  <TableCell><b>Total Amount</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedVehicleLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.date}</TableCell>
                    <TableCell>{log.personName || "-"}</TableCell>
                    <TableCell>{log.openingReading} / {log.closingReading}</TableCell>
                    <TableCell>{log.qty}L @ ₹{log.rate}</TableCell>
                    <TableCell>
                      <Typography color="secondary" fontWeight="bold">
                        {log.average} km/L
                      </Typography>
                    </TableCell>
                    <TableCell><b>₹{log.amount}</b></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFuelDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Vehicalmgmt;